import React, { useEffect } from "react";
import BtnLargeSolid from "../../buttons/BtnLargeSolid";
import styled from "styled-components";
import * as Library from "../../../../utils/Library";
import { useLocation } from "react-router";
import { MainWindowStyledPage, OrganizeDropdownSelector } from "./MainWindowHeader";

const StyledPage = styled.div`
    &.content-header,
    &.content-header * {
        box-sizing: border-box;
    }
    &.content-header {
        background: var(--backgrounds-and-lines-n-600, #10141b);
        padding: 15px 24px 15px 24px;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        flex-shrink: 0;
        height: 66px;
        position: relative;
    }
    .frame-40 {
        display: flex;
        flex-direction: row;
        gap: 8px;
        align-items: center;
        justify-content: flex-end;
        flex: 1;
        position: relative;
    }
`;

/**
 * Styled component for the page header.
 *
 * @param {Object} props - The props of the component.
 * @param {Function} props.onClick - The function to call when the button is clicked.
 * @param {String} props.btnTitle - The title of the button.
 * @param {Boolean} props.btnEnabled - The flag to enable or disable the button.
 * @param {String} [props.secondBtnTitle] - The title of the second button.
 * @param {Boolean} [props.secondBtnEnabled] - The flag to enable or disable the second button.
 * @param {Function} [props.onSecondBtnClick] - The function to call when the second button is clicked.
 * @param {Object} [props.firstOrganize] - First organize object containing organize related properties.
 * @param {boolean} [props.firstOrganize.visible=true] - If true, first organize related properties will be visible.
 * @param {string} [props.firstOrganize.usePlaceholderText="--"] - Placeholder text to be used in the dropdown.
 * @param {Array} [props.firstOrganize.dropdownData] - Data to be used in the dropdown.
 * @param {string} [props.firstOrganize.selectedValue] - Selected value in the dropdown.
 * @param {function} [props.firstOrganize.onDropdownChange] - Function to be called when dropdown value is changed.
 * @param {boolean} [props.firstOrganize.disabled=true] - If true, dropdown will be disabled.
 * @param {string} [props.firstOrganize.type="singleSelect"] - Type of the dropdown. (singleSelect or multiSelect)
 * @param {Object} [props.firstOrganize.multidropdownOptions] - Options for the multi select dropdown. View the FormField multidropdownOptions prop for more details.
 * @param {boolean} [props.firstOrganize.multidropdownOptions.useDoneButton=true] - If true, show done button in the multi select dropdown.
 * @param {boolean} [props.firstOrganize.multidropdownOptions.useSelectAll=false] - If true, show select all option in the multi select dropdown.
 * @param {string} [props.firstOrganize.multidropdownOptions.selectAllText="Select all"] - Text to be used for the select all option in the multi select dropdown.
 * @param {function} [props.firstOrganize.multidropdownOptions.placeholderTextFunction] - Function to retrieve the placeholder text based on the selected values.
 * @param {Array} [props.firstOrganize.multidropdownOptions.allItems] - All items including not selected items, for reference to the placeholderTextFunction.
 * @param {string} [props.firstOrganize.multidropdownOptions.labelAllSelected="All selected"] - Text to be used for the all selected option in the multi select dropdown.
 * @param {string} [props.firstOrganize.multidropdownOptions.labelNoneSelected="--"] - Text to be used for the none selected option in the multi select dropdown.
 * @param {boolean} [props.firstOrganize.onlyOneDisableDropdown=true] - If true, if there is only one element, dropdown will be disabled. (ONLY APPLICABLE FOR SINGLE SELECT!)
 * @param {Object} [props.secondOrganize] - Second organize object containing organize related properties.
 * @param {boolean} [props.secondOrganize.visible=false] - If true, second organize related properties will be visible.
 * @param {string} [props.secondOrganize.usePlaceholderText="--"] - Placeholder text to be used in the dropdown.
 * @param {Array} [props.secondOrganize.dropdownData] - Data to be used in the dropdown.
 * @param {string} [props.secondOrganize.selectedValue] - Selected value in the dropdown.
 * @param {function} [props.secondOrganize.onDropdownChange] - Function to be called when dropdown value is changed.
 * @param {boolean} [props.secondOrganize.disabled=true] - If true, dropdown will be disabled.
 * @param {string} [props.secondOrganize.type="singleSelect"] - Type of the dropdown. (singleSelect or multiSelect)
 * @param {Object} [props.secondOrganize.multidropdownOptions] - Options for the multi select dropdown. View the FormField multidropdownOptions prop for more details.
 * @param {boolean} [props.secondOrganize.multidropdownOptions.useDoneButton=true] - If true, show done button in the multi select dropdown.
 * @param {boolean} [props.secondOrganize.multidropdownOptions.useSelectAll=false] - If true, show select all option in the multi select dropdown.
 * @param {string} [props.secondOrganize.multidropdownOptions.selectAllText="Select all"] - Text to be used for the select all option in the multi select dropdown.
 * @param {Array} [props.secondOrganize.multidropdownOptions.allItems] - All items including not selected items, for reference to the placeholderTextFunction.
 * @param {function} [props.secondOrganize.multidropdownOptions.placeholderTextFunction] - Function to retrieve the placeholder text based on the selected values.
 * @param {string} [props.secondOrganize.multidropdownOptions.labelAllSelected="All selected"] - Text to be used for the all selected option in the multi select dropdown.
 * @param {string} [props.secondOrganize.multidropdownOptions.labelNoneSelected="--"] - Text to be used for the none selected option in the multi select dropdown.
 * @param {boolean} [props.secondOrganize.onlyOneDisableDropdown=true] - If true, if there is only one element, dropdown will be disabled. (ONLY APPLICABLE FOR SINGLE SELECT!))

 * @returns {JSX.Element} The rendered header component with buttons.
 */
export default function SaveButtonWindowHeader(props) {
    const {
        onClick,
        btnTitle,
        btnEnabled,
        secondBtnTitle,
        secondBtnEnabled,
        onSecondBtnClick = () => {},
        firstOrganize = {
            visible: true,
            usePlaceholderText: "--",
            dropdownData: undefined,
            selectedValue: undefined,
            onDropdownChange: undefined,
            disabled: true,
            type: "singleSelect", //singleSelect or multiSelect
            multidropdownOptions: {},
            onlyOneDisableDropdown: true
        },
        secondOrganize = {
            visible: false,
            usePlaceholderText: "--",
            dropdownData: undefined,
            selectedValue: undefined,
            onDropdownChange: undefined,
            disabled: true,
            type: "singleSelect", //singleSelect or multiSelect
            multidropdownOptions: {},
            onlyOneDisableDropdown: true
        }
    } = props;
    const location = useLocation();

    useEffect(() => {
        Library.scrollWindowToTop();
    }, [location]);

    return (
        <StyledPage className="content-header simple-button-content-header">
            <div className="frame-40">
                <MainWindowStyledPage className="btn-large-solid gap-8px flex">
                    <OrganizeDropdownSelector
                        visible={true}
                        organize={firstOrganize}
                        type={firstOrganize?.type}
                        tooltipTitle={"Connected to"}
                    />

                    <OrganizeDropdownSelector
                        visible={true}
                        organize={secondOrganize}
                        type={secondOrganize?.type}
                        tooltipTitle={"Filter"}
                    />

                    {secondBtnEnabled && (
                        <BtnLargeSolid
                            className="mr-7px"
                            text={secondBtnTitle}
                            disabled={!secondBtnEnabled}
                            onClick={onSecondBtnClick}
                        />
                    )}
                    <BtnLargeSolid className="mr-7px" text={btnTitle} disabled={!btnEnabled} onClick={onClick} />
                </MainWindowStyledPage>
            </div>
        </StyledPage>
    );
}
